/* eslint-disable max-lines */
import ClearIcon from "@mui/icons-material/Clear";
import { Alert, Box, Button, Container, Divider, IconButton, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import dayjs from "dayjs";
import styles from "./OpenMediaDistribution.styles";
import CircularLoaderFullPage from "src/components/common/CircularLoaderFullPage/CircularLoaderFullPage";
import SecureSyncSelect from "src/components/common/SecureSyncSelect/SecureSyncSelect";
import { getUserDetails } from "src/redux/userDetailsSlice";
import { listRetentionProfiles } from "src/services/adminService";
import { convertToLocaleUpperCase, filterData, getKeyBoardLabel } from "src/utils/commonHelper";
import { getSelectedCustomerId } from "src/services/utils";
import SecureSyncDatePicker from "src/components/common/SecureSyncDatePicker/SecureSyncDatePicker";
import PageTitle from "src/components/common/PageTitle/PageTitle";
import SecureSyncSnackBar from "src/components/common/SecureSyncSnackBar/SecureSyncSnackBar";
import SecureSyncAlert from "src/components/common/SecureSyncAlert/SecureSyncAlert";
import {
    calculateReturnDate,
    cleanErrorMap,
    dateSort,
    generateXML,
    getOpenMeidaGlobalAttribute,
    scrollTop,
    validateMediaNo,
} from "src/utils/processMediaHelper";
import AddMediaModal from "src/components/ProcessMedia/OmDistribution/AddMediaModal/AddMediaModal";
import {
    createOMDistributionList,
    getLogicalVaultCount,
    getOpenMediaStatus,
    insertLoggedEvent,
} from "src/services/processMediaService";
import OMDistributionDataTable from "src/components/ProcessMedia/OmDistribution/DataTable/OMDistributionDataTable";
import {
    formatDate,
    formatDateUsingCurrentDate,
    getCurrentDateTime,
    getDisplayFormat,
    getMinAndMaxDate,
    getServiceDateRange,
    serviceDateMapper,
} from "src/utils/dateHelper";
import { getServiceDateForCustomerService } from "src/services/requestServices";
import { noFlag, systemId, yesFlag } from "src/constants/serviceConstants";
import SecureSyncPrintModal from "src/components/common/SecureSyncPrintModal/SecureSyncPrintModal";
import { targetUrl } from "src/constants/printConstants";
import { getOmDistributionListPayload } from "src/services/printServices";
import { dateFormats, dateTimeUnits } from "src/constants/dateFormatConstants";
import useGetGlobalAttrs from "src/customHooks/useGetGlobalAttrs";
import { useSecureSyncHotKeys } from "src/customHooks";
import { getShortCutKeys } from "src/constants/shortCutKeys";
import SecureSyncCheckBox from "src/components/common/SecureSyncCheckBox/SecureSyncCheckBox";

export const OpenMediaDistribution = () => {
    const shortCutKeys = getShortCutKeys();
    useSecureSyncHotKeys({
        hotkeys: shortCutKeys.submit,
        callback: () => {
            if (!disableForm) {
                handleSubmit();
            }
        },
    });
    useSecureSyncHotKeys({
        hotkeys: shortCutKeys.prepareReport,
        callback: () => {
            if (disableForm) {
                printReport();
            }
        },
    });
    const { t } = useTranslation();
    const userDetails = useSelector(getUserDetails);
    const errorRows = useRef(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [retentionProfileList, setRetentionProfileList] = useState([]);
    const [retentionProfDropdown, setRetentionProfDropdown] = useState([]);
    const [selectedRetentionProfile, setSelectedRetentionProfile] = useState(null);
    const [isAddMediaModalOpen, setIsAddMediaModalOpen] = useState(false);
    const [mediaDataArr, setMediaDataArr] = useState([]);
    const [logicalVaultCount, setLogicalVaultCount] = useState("0");
    const [alertParams, setAlertParams] = useState({});
    const [disableForm, setDisableForm] = useState(false);
    const [addedMediaNos, setAddedMediaNos] = useState([]);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [serviceDates, setServiceDates] = useState([]);
    const [isPrintModalOpen, printModalOpen] = useState(false);
    const [printPayload, setPrintPayload] = useState(null);
    const globalAttributes = useGetGlobalAttrs(systemId.id60);
    const { displayMaxDate: maxDate } = getMinAndMaxDate();
    const minDate = getCurrentDateTime();
    const { volserMaxLength } = useMemo(() => {
        return getOpenMeidaGlobalAttribute(globalAttributes?.data?.rs0);
    }, [globalAttributes]);

    const columns = [
        {
            field: "action",
            headerName: "",
            width: "58px",
            sortable: false,
            renderCell: (params) => (
                <Box display={"flex"} gap={2} alignItems={"center"}>
                    <IconButton sx={styles.updateIcon} disabled={disableForm} disableRipple>
                        <ArticleOutlinedIcon sx={styles.iconStyles(disableForm)} />
                    </IconButton>
                    <IconButton
                        disabled={disableForm}
                        onClick={() => {
                            deleteSingleMediaType(params.id);
                        }}
                    >
                        <ClearIcon sx={styles.ClearIcon} />
                    </IconButton>
                </Box>
            ),
        },
        {
            field: "seq",
            headerName: convertToLocaleUpperCase(t("omDistribution.enteredSequence")),
            sortable: true,
        },
        {
            field: "mediaNo",
            headerName: convertToLocaleUpperCase(t("omDistribution.mediaNumber")),
            renderCell: (param) => {
                return (
                    <Box sx={styles.editableTableCell}>
                        <TextField
                            key={param.id}
                            label={t("omDistribution.mediaNumber")}
                            name="mediaNo"
                            fullWidth
                            required
                            disabled={disableForm}
                            value={param.mediaNo}
                            inputProps={{ maxLength: volserMaxLength }}
                            onChange={(e) => {
                                const newMedia = e.target.value;
                                const validateResult = validateMediaNo(
                                    newMedia,
                                    t,
                                    volserMaxLength,
                                    mediaDataArr,
                                    param.id
                                );

                                const newMediaDataArr = mediaDataArr.map((item) =>
                                    param.id === item.id ? { ...item, mediaNo: newMedia } : item
                                );
                                setMediaDataArr(newMediaDataArr);
                                const cleanedErrorMap = cleanErrorMap(
                                    newMediaDataArr,
                                    errorRows.current,
                                    t,
                                    volserMaxLength
                                );
                                errorRows.current = cleanedErrorMap;

                                if (validateResult) {
                                    errorRows.current.set(param.id, validateResult);
                                } else {
                                    errorRows.current.delete(param.id);
                                }
                            }}
                            onBlur={async () => {
                                setIsLoading(true);
                                try {
                                    const openMediaStatusRes = await getOpenMediaStatus(generateXML([param.mediaNo]));
                                    if (!openMediaStatusRes.failure) {
                                        const newMediaDataArr = mediaDataArr.map((item) =>
                                            param.id === item.id
                                                ? {
                                                      ...item,
                                                      openMediaStatus:
                                                          openMediaStatusRes?.rs0[0].open_media_status || "",
                                                  }
                                                : item
                                        );
                                        setMediaDataArr(newMediaDataArr);
                                    }
                                } catch (error) {
                                    console.error("Get Open Media Status - catch error: ", error);
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            error={errorRows.current.has(param.id)}
                            helperText={errorRows.current.get(param.id)}
                        />
                    </Box>
                );
            },
        },
        {
            field: "returnDate",
            headerName: convertToLocaleUpperCase(t("omDistribution.returnDate")),
            renderCell: (param) => {
                const isIndefiniteDate = param.returnDate === t("omDistribution.indefinite").toLowerCase();
                return (
                    <Box sx={styles.returnDateTableCell}>
                        <SecureSyncDatePicker
                            inputPropId={param.id}
                            key={`Date-${param.id}`}
                            name="returnDate"
                            disabled={disableForm}
                            label={t("omDistribution.returnDate")}
                            value={
                                isIndefiniteDate
                                    ? ""
                                    : param.returnDate
                                      ? dayjs(param.returnDate, getDisplayFormat())
                                      : ""
                            }
                            onChange={(date) => {
                                let dateValue = "";
                                if (date !== "Invalid Date") {
                                    dateValue = date;
                                }
                                const newMediaDataArr = mediaDataArr.map((item) =>
                                    param.id === item.id ? { ...item, returnDate: dateValue } : item
                                );
                                setMediaDataArr(newMediaDataArr);
                            }}
                            disablePast
                            getDateProp={(dateProp) => {
                                const isServiceDate = serviceDates.find((serviceDate) =>
                                    serviceDate.isSame(dateProp, dateTimeUnits.day)
                                );
                                return { highlight: isServiceDate };
                            }}
                            customDateValue={isIndefiniteDate ? t("omDistribution.indefinite") : ""}
                        />
                        <SecureSyncCheckBox
                            key={param.id}
                            label={t("omDistribution.indefinite")}
                            disabled={disableForm}
                            name="indefinite"
                            checked={isIndefiniteDate ? true : false}
                            onChange={(e) => {
                                let dateValue = "";
                                if (e.target.checked) {
                                    dateValue = t("omDistribution.indefinite").toLowerCase();
                                }
                                const newMediaDataArr = mediaDataArr.map((item) =>
                                    param.id === item.id ? { ...item, returnDate: dateValue } : item
                                );
                                setMediaDataArr(newMediaDataArr);
                            }}
                        />
                    </Box>
                );
            },
        },
        {
            field: "drpCode",
            headerName: convertToLocaleUpperCase(t("omDistribution.drpCode")),
            renderCell: (param) => {
                return (
                    <Box sx={styles.editableTableCell}>
                        <TextField
                            key={param.id}
                            disabled={disableForm}
                            id="drpCode"
                            name="drpCode"
                            label={t("omDistribution.drpCode")}
                            aria-label="DRPC"
                            fullWidth
                            value={param.drpCode}
                            inputProps={{ maxLength: 10, style: { textTransform: "uppercase" } }}
                            onChange={(event) => {
                                const newMediaDataArr = mediaDataArr.map((item) =>
                                    param.id === item.id ? { ...item, drpCode: event.target.value.toUpperCase() } : item
                                );
                                setMediaDataArr(newMediaDataArr);
                            }}
                        />
                    </Box>
                );
            },
        },
        {
            field: "openMediaStatus",
            headerName: convertToLocaleUpperCase(t("omDistribution.openMediaStatus")),
        },
        {
            field: "description",
            headerName: convertToLocaleUpperCase(t("omDistribution.description")),
            renderCell: (param) => {
                return (
                    <Box sx={styles.editableTableCell}>
                        <TextField
                            key={param.id}
                            disabled={disableForm}
                            id="description"
                            name="description"
                            label={t("omDistribution.description")}
                            aria-label="description"
                            multiline
                            rows={3}
                            fullWidth
                            value={param.description}
                            onChange={(e) => {
                                const newMediaDataArr = mediaDataArr.map((item) =>
                                    param.id === item.id ? { ...item, description: e.target.value } : item
                                );
                                setMediaDataArr(newMediaDataArr);
                            }}
                        />
                    </Box>
                );
            },
        },
    ];

    const getRowId = (row) => mediaDataArr.indexOf(row);
    const { fromDate, toDate } = getServiceDateRange();

    const handleCloseSnackbar = () => {
        setAlertParams({
            messageCode: "",
            showAlert: false,
            error: false,
        });
    };

    const deleteSingleMediaType = (id) => {
        const filteredDataArr = mediaDataArr.filter((value) => value.id !== id);
        setMediaDataArr(filteredDataArr);
        setAddedMediaNos(filteredDataArr.map((item) => item.mediaNo));
    };

    useEffect(() => {
        getLogicalVaultCountSer();
        getRetentionProfiles();
        setFieldValue("volserMaxLength", volserMaxLength);
        const getServiceDateList = async () => {
            setIsLoading(true);
            try {
                const response = await getServiceDateForCustomerService({
                    fromDate: fromDate,
                    toDate: toDate,
                    rescheduleFlag: noFlag,
                    addonCutoffFlag: "",
                });
                if (!response.failure) {
                    if (response?.resultSet0?.length > 0) {
                        setServiceDates(serviceDateMapper(response?.resultSet0));
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };
        getServiceDateList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volserMaxLength]);

    const openAddMediaModalHandler = () => {
        setIsAddMediaModalOpen(true);
    };

    const closeAddMediaModalHandler = () => {
        setIsAddMediaModalOpen(false);
    };

    const submitAddMediaModalHandler = async (mediaArr) => {
        setIsLoading(true);
        const openMediaStatusRes = await getOpenMediaStatus(generateXML(mediaArr));

        const newMediaArr = mediaArr.map((value) => {
            let mediaStatus = "";
            if (!openMediaStatusRes.failure) {
                mediaStatus =
                    filterData(openMediaStatusRes?.rs0, "open_media_number", value.mediaList)[0]?.open_media_status ||
                    "";
            }
            return {
                id: value.id,
                mediaNo: value.mediaList,
                returnDate: values.returnDate,
                drpCode: values.drpCode,
                openMediaStatus: mediaStatus,
                description: values.description,
            };
        });

        const allElementArray = [...mediaDataArr, ...newMediaArr];
        const sortedArray = dateSort(allElementArray, "returnDate", t("omDistribution.indefinite"));
        setAddedMediaNos(sortedArray.map((item) => item.mediaNo));
        setMediaDataArr(sortedArray);
        closeAddMediaModalHandler();
        setIsLoading(false);
    };
    const { values, handleChange, handleBlur, handleSubmit, setFieldValue, initialValues, setValues } = useFormik({
        initialValues: {
            serviceDate: "",
            retentionProfile: "",
            isIndefiniteRetention: false,
            returnDate: "",
            drpCode: "",
            description: "",
            volserMaxLength: "20",
            customerFileSectionId: "",
        },
        onSubmit: async (values) => {
            if (values.serviceDate && mediaDataArr.length > 0) {
                setIsLoading(true);
                const volserListString = mediaDataArr?.map((value) => value.mediaNo).join("\t");
                const returnDateListString = mediaDataArr
                    .map((value) => {
                        if (
                            value.returnDate === t("omDistribution.indefinite").toLowerCase() ||
                            value.returnDate === ""
                        ) {
                            return t("omDistribution.indefinite");
                        } else {
                            return formatDateUsingCurrentDate({
                                date: value.returnDate,
                                currentFormat: getDisplayFormat(),
                                convertFormat: dateFormats.displayDate,
                            });
                        }
                    })
                    .join("\t");
                const descrListString = mediaDataArr.map((value) => value.description).join("\t");
                const drpCodeListString = mediaDataArr.map((value) => value.drpCode).join("\t");
                const indefiniteString = t("omDistribution.indefinite");
                try {
                    const distributionRes = await createOMDistributionList(
                        getSelectedCustomerId(),
                        userDetails.preferred_username,
                        volserListString,
                        returnDateListString,
                        descrListString,
                        drpCodeListString,
                        indefiniteString,
                        formatDateUsingCurrentDate({
                            date: values.serviceDate,
                            currentFormat: getDisplayFormat(),
                            convertFormat: dateFormats.displayDate,
                        })
                    );

                    if (distributionRes.failure) {
                        setAlertParams({
                            messageCode: distributionRes.errorCode,
                            showAlert: true,
                            error: true,
                        });
                        scrollTop();
                    } else {
                        setAlertParams({
                            messageCode: 63033,
                            showAlert: true,
                            error: false,
                        });
                        scrollTop();
                        setDisableForm(true);
                        setShowErrorAlert(false);
                        setFieldValue("customerFileSectionId", distributionRes.customerFileSectionId);
                        await createLog(volserListString.length);
                    }
                } catch (error) {
                    console.error("OM Distribution OnSubmit - catch error: ", error);
                }
                setIsLoading(false);
            } else {
                scrollTop();
                setShowErrorAlert(true);
            }
        },
        enableReinitialize: true,
    });
    const getLogicalVaultCountSer = async () => {
        setIsLoading(true);
        try {
            const res = await getLogicalVaultCount();
            setLogicalVaultCount(res.rs0[0].count);
        } catch (error) {
            console.error("get Logical vault count - catch error: ", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getRetentionProfiles = async () => {
        setIsLoading(true);
        try {
            const getRetentionProfilesRes = await listRetentionProfiles(
                t("omDistribution.indefinite"),
                t("localizedServiceDetails.zeroDays"),
                "5"
            );
            const retentionDropArr = mapRetentionProfiles(getRetentionProfilesRes?.rs0);
            setRetentionProfDropdown(retentionDropArr);
            setRetentionProfileList(getRetentionProfilesRes?.rs0);
            setFieldValue("retentionProfile", "");
            const defaultRetentionProfile = filterData(getRetentionProfilesRes?.rs0, "default_om_dist_list_flag", "Y");
            if (defaultRetentionProfile.length > 0) {
                selectRetentionProfile(getRetentionProfilesRes?.rs0, defaultRetentionProfile[0]?.retention_profile_id);
            }
        } catch (error) {
            console.error("get Retention Profiles - catch error: ", error);
        } finally {
            setIsLoading(false);
        }
    };

    const mapRetentionProfiles = (retArr) => {
        if (retArr.length > 0) {
            return retArr.map((value) => {
                return { id: value.retention_profile_id, label: value.profile_name, value: value.retention_profile_id };
            });
        } else {
            return [{ id: "1", value: "x", label: t("common.defaultDropdownOption") }];
        }
    };

    const handleCheckBoxChange = (e) => {
        if (e.target.checked) {
            setFieldValue("returnDate", t("omDistribution.indefinite").toLowerCase());
        }
    };

    const isRetentionProfileDefault =
        retentionProfDropdown.length === 1 && retentionProfDropdown[0].label === t("common.defaultDropdownOption");

    const selectRetentionProfile = (retArr, selectedRetentionProfId) => {
        const defaultRetentionProf = filterData(retArr, "retention_profile_id", selectedRetentionProfId);
        if (defaultRetentionProf.length > 0) {
            const isIndefiniteRetentionProfile = defaultRetentionProf[0].indefinite_retention_flag === yesFlag;
            setSelectedRetentionProfile(defaultRetentionProf[0]);
            setFieldValue("retentionProfile", defaultRetentionProf[0].retention_profile_id);
            setFieldValue("isIndefiniteRetention", isIndefiniteRetentionProfile);
            setFieldValue("drpCode", defaultRetentionProf[0].drp_code.toLocaleUpperCase());
            setFieldValue("description", defaultRetentionProf[0].media_descr);
            setFieldValue(
                "returnDate",
                isIndefiniteRetentionProfile
                    ? t("omDistribution.indefinite").toLowerCase()
                    : calculateReturnDate(values.serviceDate, defaultRetentionProf[0], t("omDistribution.indefinite"))
            );
        } else {
            const savedServiceDate = values.serviceDate;
            setValues({ ...initialValues, serviceDate: savedServiceDate });
            setSelectedRetentionProfile(null);
        }
    };

    const serviceDateChange = (date) => {
        setFieldValue("serviceDate", date);
        setFieldValue(
            "returnDate",
            calculateReturnDate(date, selectedRetentionProfile, t("omDistribution.indefinite").toLowerCase())
        );
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            // Check for Ctrl (or Cmd) + V
            if ((event.ctrlKey || event.metaKey) && event.key === "v") {
                if (navigator.clipboard && navigator.clipboard.readText) {
                    // Read the text from the clipboard
                    navigator.clipboard
                        .readText()
                        .then((text) => {
                            const dateFromString = dayjs(text).format(getDisplayFormat());
                            if (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA") {
                                if (event.target.name === "serviceDate") {
                                    serviceDateChange(dateFromString);
                                } else {
                                    const returnDataId = event?.target?.getAttribute("inputPropsId");
                                    if (returnDataId) {
                                        let dateValue = "";
                                        if (dateFromString !== "Invalid Date") {
                                            dateValue = dateFromString;
                                        }
                                        const newMediaDataArr = mediaDataArr.map((item) =>
                                            returnDataId === item.id ? { ...item, returnDate: dateValue } : item
                                        );
                                        setMediaDataArr(newMediaDataArr);
                                    }
                                }
                            }
                        })
                        .catch((err) => {
                            console.error("Failed to read clipboard contents: ", err);
                        });
                }
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaDataArr]);

    const returnDateChange = (date) => {
        if (date !== "Invalid Date") {
            setFieldValue("returnDate", date);
        } else {
            setFieldValue("returnDate", "");
        }
    };
    const retentionProfChangeHandler = (event) => {
        setFieldValue("retentionProfile", event.target.value);
    };

    useEffect(() => {
        selectRetentionProfile(retentionProfileList, values.retentionProfile);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.retentionProfile]);

    const createLog = async (importedItemCount) => {
        setIsLoading(true);
        try {
            const description = `OM Distribution List - Submit ${importedItemCount} items. Customer_Id = ${getSelectedCustomerId()}`;
            const comment = `Statistics ignore deletes`;
            await insertLoggedEvent(description, comment);
        } catch (error) {
            console.error("createLog - catch error: ", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReset = () => {
        setMediaDataArr([]);
        setAddedMediaNos([]);
    };

    const isReturnDateIndefinite = values.returnDate === t("omDistribution.indefinite").toLowerCase();

    const returnDateIndefinitePhase = () => {
        if (isReturnDateIndefinite) {
            return t("omDistribution.indefinite");
        }

        return "";
    };

    const printReport = () => {
        const mediaList = [];
        mediaDataArr.forEach((e) => {
            mediaList.push(e.mediaNo);
        });
        const drpCode = mediaDataArr[0] && mediaDataArr[0].drpCode ? mediaDataArr[0].drpCode : "";
        const returnDate = isReturnDateIndefinite
            ? t("omDistribution.indefinite")
            : mediaDataArr[0] && mediaDataArr[0].returnDate
              ? formatDateUsingCurrentDate({
                    date: mediaDataArr[0].returnDate,
                    currentFormat: getDisplayFormat(),
                    convertFormat: dateFormats.isoDate,
                })
              : "";
        const description = mediaDataArr[0] && mediaDataArr[0].description ? mediaDataArr[0].description : "";
        setPrintPayload(
            getOmDistributionListPayload({
                customerFileSectionId: values.customerFileSectionId,
                omListId: mediaList.toString(),
                serviceDate: formatDateUsingCurrentDate({
                    date: values.serviceDate,
                    currentFormat: getDisplayFormat(),
                    convertFormat: dateFormats.isoDate,
                }),
                returnDate,
                drpCode,
                description,
            })
        );
        printModalOpen(true);
    };
    return (
        <>
            <CircularLoaderFullPage show={isLoading} />
            <Container>
                <form onSubmit={handleSubmit}>
                    <Box sx={styles.container}>
                        <Box sx={styles.innerBox}>
                            <PageTitle>{t("omDistribution.openMediaDistributionList")}</PageTitle>
                            <div className="notranslate">
                                <SecureSyncSnackBar
                                    openSnackBar={alertParams.showAlert}
                                    message={
                                        <Typography fontSize={12} maxWidth={440}>
                                            <Trans
                                                i18nKey={`apiErrorMessages.${alertParams?.messageCode}`}
                                                values={{ buttonLabel: t("buttonLabels.prepareReport") }}
                                            />
                                        </Typography>
                                    }
                                    severity={alertParams?.error ? "error" : "success"}
                                    onClose={handleCloseSnackbar}
                                />
                            </div>

                            {showErrorAlert && (!values.serviceDate || mediaDataArr.length === 0) && (
                                <Box sx={styles.marginBottomBreak}>
                                    <SecureSyncAlert
                                        message={
                                            <Typography sx={styles.fontWeight500}>
                                                {t("omDistribution.followingError")}
                                                <br />
                                                {!values.serviceDate && (
                                                    <Box sx={styles.errorMsgs}>
                                                        {t("omDistribution.serviceDateMandatory")}
                                                    </Box>
                                                )}
                                                {mediaDataArr.length === 0 && (
                                                    <Box sx={styles.errorMsgs}>{t("omDistribution.noOMList")}</Box>
                                                )}
                                                {!isReturnDateIndefinite &&
                                                    !dayjs(values.returnDate, getDisplayFormat()).isValid() && (
                                                        <Box sx={styles.errorMsgs}>
                                                            {t("messages.incompleteAutoCompleteReturnDate", {
                                                                format: getDisplayFormat(),
                                                                fromDate: formatDate(minDate),
                                                                toDate: maxDate,
                                                            })}
                                                        </Box>
                                                    )}
                                            </Typography>
                                        }
                                        severity="error"
                                        onClose={() => {
                                            setShowErrorAlert(false);
                                        }}
                                    />
                                </Box>
                            )}
                            {logicalVaultCount === "0" ? (
                                <Alert severity="info">{t("omDistribution.thisCustomerIsNotConfigured")}</Alert>
                            ) : (
                                <>
                                    {!showErrorAlert && (
                                        <>
                                            <Typography sx={styles.fontWeight500}>
                                                {t("omDistribution.selectAServiceData")}
                                            </Typography>
                                            <Typography sx={styles.fontWeight500}>
                                                <Trans i18nKey={"omDistribution.clickSubmit"} />
                                            </Typography>
                                        </>
                                    )}
                                    <br />
                                    <Divider />
                                    <Typography variant="h5" sx={styles.sectionTitle}>
                                        {t("omDistribution.serviceDate")}
                                    </Typography>
                                    <Box component="div" width={"25%"} paddingRight={1}>
                                        <SecureSyncDatePicker
                                            name="serviceDate"
                                            label={t("omDistribution.serviceDate") + "*"}
                                            value={values.serviceDate}
                                            disabled={disableForm}
                                            onChange={serviceDateChange}
                                            onBlur={handleBlur}
                                            disablePast
                                            getDateProp={(dateProp) => {
                                                const isServiceDate = serviceDates.find((serviceDate) =>
                                                    serviceDate.isSame(dateProp, dateTimeUnits.day)
                                                );
                                                return { highlight: isServiceDate };
                                            }}
                                        />
                                    </Box>
                                    <Typography sx={styles.switchDescription}>
                                        {t("omDistribution.selectTheDateOM")}
                                    </Typography>
                                    <Divider />
                                    <Typography variant="h5" sx={styles.sectionTitle}>
                                        {t("omDistribution.retentionProfile")}
                                    </Typography>
                                    <Typography sx={styles.instructionText}>
                                        {t("omDistribution.selectARetentionProfile")}
                                    </Typography>
                                    <Box component="div" sx={styles.marginBottomBreak} width={"50%"}>
                                        <SecureSyncSelect
                                            name="retentionProfile"
                                            type="outlined"
                                            label={t("omDistribution.retentionProfile")}
                                            options={retentionProfDropdown}
                                            showBlankOption={!isRetentionProfileDefault}
                                            value={values.retentionProfile}
                                            onChange={retentionProfChangeHandler}
                                            defaultOption={
                                                isRetentionProfileDefault ? t("common.defaultDropdownOption") : ""
                                            }
                                            disabled={disableForm}
                                        />
                                    </Box>
                                    <Divider />
                                    <Typography variant="h5" sx={styles.sectionTitle}>
                                        {t("omDistribution.autoCompleteValues")}
                                    </Typography>
                                    <Typography sx={styles.instructionText}>
                                        {t("omDistribution.enteringAutoCompleteValues")}
                                    </Typography>
                                    <Box component="div" sx={styles.flexRow50}>
                                        <Box component="div" style={{ flex: 1 }}>
                                            <SecureSyncDatePicker
                                                name="returnDate"
                                                label={t("omDistribution.returnDate")}
                                                value={isReturnDateIndefinite ? "" : values.returnDate}
                                                onChange={returnDateChange}
                                                onBlur={handleBlur}
                                                disabled={disableForm}
                                                disablePast
                                                getDateProp={(dateProp) => {
                                                    const isServiceDate = serviceDates.find((serviceDate) =>
                                                        serviceDate.isSame(dateProp, dateTimeUnits.day)
                                                    );
                                                    return { highlight: isServiceDate };
                                                }}
                                                customDateValue={returnDateIndefinitePhase()}
                                            />
                                        </Box>
                                        <Box component="div" style={{ flex: 1 }}>
                                            <SecureSyncCheckBox
                                                label={t("omDistribution.indefinite")}
                                                name="indefinite"
                                                disabled={disableForm}
                                                checked={isReturnDateIndefinite ? true : false}
                                                onChange={(e) => handleCheckBoxChange(e)}
                                            />
                                        </Box>

                                        <Box component="div" style={{ flex: 1 }}>
                                            <TextField
                                                name="drpCode"
                                                label={t("omDistribution.drpCode")}
                                                inputProps={{ maxLength: 10, style: { textTransform: "uppercase" } }}
                                                fullWidth
                                                onChange={(event) => {
                                                    setFieldValue("drpCode", event.target.value.toLocaleUpperCase());
                                                }}
                                                value={values.drpCode}
                                                disabled={disableForm}
                                            />
                                        </Box>
                                    </Box>
                                    <Box component="div" sx={styles.descriptionField}>
                                        <TextField
                                            name="description"
                                            label={t("omDistribution.description")}
                                            multiline
                                            rows={4}
                                            fullWidth
                                            onChange={handleChange}
                                            value={values.description}
                                            disabled={disableForm}
                                        />
                                    </Box>
                                    <Divider />
                                    <Typography variant="h5" sx={styles.sectionTitle}>
                                        {t("omDistribution.openMediaDistributionList")}
                                    </Typography>
                                    <Typography sx={styles.instructionText}>
                                        <Trans i18nKey={"omDistribution.clickAddMedia"} />
                                    </Typography>
                                    <Box sx={styles.marginBottomBreak}>
                                        <OMDistributionDataTable
                                            columns={columns}
                                            mediaData={mediaDataArr}
                                            openAddMediaModalHandler={openAddMediaModalHandler}
                                            getRowId={getRowId}
                                            disableForm={disableForm}
                                        />
                                    </Box>
                                    <Divider />
                                    <Box sx={styles.buttonContainer}>
                                        <Button variant="outlined" onClick={handleReset} disabled={disableForm}>
                                            {t("buttonLabels.deleteAll")}
                                        </Button>
                                        <Button
                                            title={getKeyBoardLabel(shortCutKeys.submit)}
                                            variant="contained"
                                            type="submit"
                                            disabled={disableForm}
                                        >
                                            {t("common.submit")}
                                        </Button>
                                        {disableForm && (
                                            <Button
                                                title={getKeyBoardLabel(shortCutKeys.prepareReport)}
                                                onClick={printReport}
                                                variant="contained"
                                            >
                                                {t("buttonLabels.prepareReport")}
                                            </Button>
                                        )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </form>
            </Container>
            {isAddMediaModalOpen && (
                <AddMediaModal
                    addedMediaNos={addedMediaNos}
                    onClose={closeAddMediaModalHandler}
                    onSubmit={submitAddMediaModalHandler}
                    volserMaxLength={values.volserMaxLength}
                />
            )}
            <SecureSyncPrintModal
                open={isPrintModalOpen}
                payload={printPayload}
                onClose={() => printModalOpen(false)}
                targetReport={targetUrl.omDistributionListReport}
            />
        </>
    );
};
